"use client"

import { useState } from "react"
import { Calendar as CalendarIcon, Clock, Plus, Edit2, Trash2, AlertTriangle } from "react-feather"
import Card from "./ui/Card"
import Button from "./ui/Button"
import "../styles/Schedule.css"

// Mock data
const maintenanceEvents = [
  {
    id: 1,
    title: "Panel Cleaning",
    date: "2024-01-15",
    time: "09:00",
    status: "scheduled",
    technician: "John Smith",
    notes: "Regular cleaning of all solar panels",
  },
  {
    id: 2,
    title: "Inverter Inspection",
    date: "2024-01-28",
    time: "10:30",
    status: "scheduled",
    technician: "Sarah Johnson",
    notes: "Annual inspection of inverter systems",
  },
  {
    id: 3,
    title: "Battery Maintenance",
    date: "2024-02-10",
    time: "14:00",
    status: "scheduled",
    technician: "Mike Wilson",
    notes: "Check battery health and connections",
  },
]

const completedEvents = [
  {
    id: 101,
    title: "System Upgrade",
    date: "2023-12-05",
    time: "08:00",
    status: "completed",
    technician: "Tech Team",
    notes: "Firmware upgrade for all system components",
  },
  {
    id: 102,
    title: "Panel Cleaning",
    date: "2023-11-15",
    time: "09:00",
    status: "completed",
    technician: "John Smith",
    notes: "Regular cleaning of all solar panels",
  },
]

const months = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
]

function Schedule() {
  const [currentDate, setCurrentDate] = useState(new Date())
  const [showEventForm, setShowEventForm] = useState(false)
  const [selectedEvent, setSelectedEvent] = useState(null)

  const currentYear = currentDate.getFullYear()
  const currentMonth = currentDate.getMonth()

  const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate()
  const firstDayOfMonth = new Date(currentYear, currentMonth, 1).getDay()

  // Generate calendar days
  const calendarDays = []

  // Add empty cells for days before the first day of the month
  for (let i = 0; i < firstDayOfMonth; i++) {
    calendarDays.push({ day: "", empty: true })
  }

  // Add days of the month
  for (let day = 1; day <= daysInMonth; day++) {
    const date = `${currentYear}-${String(currentMonth + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`
    const events = maintenanceEvents.filter((event) => event.date === date)
    calendarDays.push({ day, date, events, empty: false })
  }

  const goToPreviousMonth = () => {
    setCurrentDate(new Date(currentYear, currentMonth - 1, 1))
  }

  const goToNextMonth = () => {
    setCurrentDate(new Date(currentYear, currentMonth + 1, 1))
  }

  const handleAddEvent = () => {
    setSelectedEvent(null)
    setShowEventForm(true)
  }

  const handleEditEvent = (event) => {
    setSelectedEvent(event)
    setShowEventForm(true)
  }

  const handleCloseForm = () => {
    setShowEventForm(false)
    setSelectedEvent(null)
  }

  const handleSubmitForm = (e) => {
    e.preventDefault()
    // In a real app, this would save the event data
    setShowEventForm(false)
    setSelectedEvent(null)
  }

  return (
    <div className="schedule-page">
      <div className="page-header">
        <h1>Maintenance Schedule</h1>
        <div className="header-actions">
          <Button variant="primary" onClick={handleAddEvent}>
            <Plus size={16} />
            <span>Add Event</span>
          </Button>
        </div>
      </div>

      <div className="schedule-content">
        <Card className="calendar-card">
          <div className="calendar-header">
            <button className="month-nav-btn" onClick={goToPreviousMonth}>
              &lt;
            </button>
            <h2>
              {months[currentMonth]} {currentYear}
            </h2>
            <button className="month-nav-btn" onClick={goToNextMonth}>
              &gt;
            </button>
          </div>
          <div className="calendar-grid">
            <div className="calendar-day-header">Sun</div>
            <div className="calendar-day-header">Mon</div>
            <div className="calendar-day-header">Tue</div>
            <div className="calendar-day-header">Wed</div>
            <div className="calendar-day-header">Thu</div>
            <div className="calendar-day-header">Fri</div>
            <div className="calendar-day-header">Sat</div>

            {calendarDays.map((dayData, index) => (
              <div
                key={index}
                className={`calendar-day ${dayData.empty ? "empty" : ""} ${dayData.events?.length ? "has-events" : ""}`}
              >
                {dayData.day}
                {dayData.events?.map((event) => (
                  <div key={event.id} className="calendar-event" onClick={() => handleEditEvent(event)}>
                    {event.title}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </Card>

        <div className="events-section">
          <Card className="events-card">
            <h2>Upcoming Maintenance</h2>
            <div className="events-list">
              {maintenanceEvents.length === 0 ? (
                <div className="no-events">No upcoming maintenance scheduled</div>
              ) : (
                maintenanceEvents.map((event) => (
                  <div key={event.id} className="event-item">
                    <div className="event-date">
                      <CalendarIcon size={16} />
                      <span>{event.date}</span>
                    </div>
                    <div className="event-time">
                      <Clock size={16} />
                      <span>{event.time}</span>
                    </div>
                    <div className="event-title">{event.title}</div>
                    <div className="event-technician">{event.technician}</div>
                    <div className="event-actions">
                      <button className="event-action-btn edit" onClick={() => handleEditEvent(event)}>
                        <Edit2 size={16} />
                      </button>
                      <button className="event-action-btn delete">
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </Card>

          <Card className="events-card">
            <h2>Completed Maintenance</h2>
            <div className="events-list">
              {completedEvents.map((event) => (
                <div key={event.id} className="event-item completed">
                  <div className="event-date">
                    <CalendarIcon size={16} />
                    <span>{event.date}</span>
                  </div>
                  <div className="event-time">
                    <Clock size={16} />
                    <span>{event.time}</span>
                  </div>
                  <div className="event-title">{event.title}</div>
                  <div className="event-technician">{event.technician}</div>
                  <div className="event-status">Completed</div>
                </div>
              ))}
            </div>
          </Card>
        </div>

        <Card className="maintenance-alerts-card">
          <div className="card-header with-icon">
            <AlertTriangle className="alert-icon" />
            <h3>Maintenance Alerts</h3>
          </div>
          <div className="alerts-list">
            <div className="alert-item">
              <div className="alert-icon-container warning">
                <AlertTriangle size={16} />
              </div>
              <div className="alert-content">
                <div className="alert-title">Panel Cleaning Due</div>
                <div className="alert-message">Panel cleaning is recommended within the next 2 weeks</div>
              </div>
              <Button variant="outline" size="sm">
                Schedule
              </Button>
            </div>
            <div className="alert-item">
              <div className="alert-icon-container warning">
                <AlertTriangle size={16} />
              </div>
              <div className="alert-content">
                <div className="alert-title">Inverter Maintenance</div>
                <div className="alert-message">Annual inverter maintenance is due in January</div>
              </div>
              <Button variant="outline" size="sm">
                Schedule
              </Button>
            </div>
          </div>
        </Card>
      </div>

      {showEventForm && (
        <div className="modal-overlay">
          <div className="event-form-modal">
            <div className="modal-header">
              <h2>{selectedEvent ? "Edit Event" : "Add New Event"}</h2>
              <button className="close-btn" onClick={handleCloseForm}>
                ×
              </button>
            </div>
            <form onSubmit={handleSubmitForm}>
              <div className="form-group">
                <label htmlFor="event-title">Title</label>
                <input type="text" id="event-title" defaultValue={selectedEvent?.title || ""} required />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="event-date">Date</label>
                  <input type="date" id="event-date" defaultValue={selectedEvent?.date || ""} required />
                </div>
                <div className="form-group">
                  <label htmlFor="event-time">Time</label>
                  <input type="time" id="event-time" defaultValue={selectedEvent?.time || ""} required />
                </div>
              </div>
              <div className="form-group">
                <label htmlFor="event-technician">Technician</label>
                <input type="text" id="event-technician" defaultValue={selectedEvent?.technician || ""} required />
              </div>
              <div className="form-group">
                <label htmlFor="event-notes">Notes</label>
                <textarea id="event-notes" rows="3" defaultValue={selectedEvent?.notes || ""}></textarea>
              </div>
              <div className="form-actions">
                <Button variant="outline" type="button" onClick={handleCloseForm}>
                  Cancel
                </Button>
                <Button variant="primary" type="submit">
                  {selectedEvent ? "Update Event" : "Add Event"}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}

export default Schedule

